/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  BoxProps,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { UUID } from "crypto";
import { useGetFilesCatalogQuery } from "@api/catalogs";
import {
  DocumentResponse,
  ErrorResponse,
  CatalogFile,
  PayloadSignUrl,
  PayloadDocument,
  PayloadUploadFile,
} from "@interfaces/index";
import {
  useLazyGetSignUrlToUploadQuery,
  usePatchDocumentMutation,
  usePostDocumentMutation,
  useUploadFileMutation,
} from "@api/documents";
import { RootState, useAppDispatch } from "@store/store";
import { setUploadModalErrorVisible } from "@store/slices/appSlice";
import CloseIcon from "@mui/icons-material/Close";
import Done from "@assets/img/done.svg";
import { useSelector } from "react-redux";
import {
  useLazyGetSignUrlToUploadSecondGuaranteeQuery,
  usePatchDocumentSecondGuaranteeMutation,
  usePostDocumentSecondGuaranteeMutation,
  useUploadFileSecondGuaranteeMutation,
} from "@api/second_guarantee_api/secondGuaranteeApi";

interface CustomUploadFileProps extends BoxProps {
  inputName: string;
  disabled?: boolean;
  uploadedFile?: DocumentResponse;
  setUploadedFile: (file: DocumentResponse | undefined) => void;
  optionalName?: string;
  optionalDescription?: string;
  index?: number;
  metadata: {
    fileCode: string;
    uploadType: "client" | "person";
    clientDetailsId?: UUID;
    personType?:
      | "otro"
      | "empleado"
      | "broker"
      | "accionista_mayoritario"
      | "aval"
      | "representante_legal"
      | "representante_legal_propietario_real"
      | "accionista_mayoritario_propietario_real"
      | "representante_legal_accionista_mayoritario_propietario_real"
      | "representante_legal_accionista_mayoritario"
      | "propietario_real"
      | "segundo_aval";

    personId?: UUID;
    guaranteeId?: UUID;
  };
  hiddeDivider?: boolean;
  required?: boolean;
  uploadService?: "default" | "second_guarantee";
}

const CustomUploadFileV2 = ({
  inputName,
  uploadedFile,
  setUploadedFile,
  optionalName,
  optionalDescription,
  metadata,
  disabled,
  hiddeDivider,
  required,
  uploadService,
  ...props
}: CustomUploadFileProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);

  const [fileCategory, setFileCategory] = useState<CatalogFile>();
  const [loading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const documentConfig = useGetFilesCatalogQuery();
  const [triggerUploadFile] = useUploadFileMutation();
  const [triggerPostDocument] = usePostDocumentMutation();
  const [triggerPatchDocument] = usePatchDocumentMutation();
  const [triggerGetSignUrl] = useLazyGetSignUrlToUploadQuery();

  // const documentConfig = useGetFilesCatalogSecondGuaranteeQuery();
  const [triggerUploadFileSecondGuarantee] = useUploadFileSecondGuaranteeMutation();
  const [triggerGetSignUrlSecondGuarantee] = useLazyGetSignUrlToUploadSecondGuaranteeQuery();
  const [triggerPostDocumentSecondGuarantee] = usePostDocumentSecondGuaranteeMutation();
  const [triggerPatchDocumentSecondGuarantee] = usePatchDocumentSecondGuaranteeMutation();

  const handleOnChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.item(0);
    const fileName: string = file ? file.name : "";
    try {
      if (file && fileCategory) {
        setLoading(true);

        // generar la url de subida
        const payloadSignUrl: PayloadSignUrl = {
          upload_type: metadata.uploadType,
          file_name: file.name,
          regimen: application.tax_system_type.toLocaleLowerCase(),
          client_details_id: metadata.clientDetailsId ? metadata.clientDetailsId : undefined,
          person_id: metadata.personId ? metadata.personId : undefined,
          guarantee_id: metadata.guaranteeId ? metadata.guaranteeId : undefined,
          file_category_id: fileCategory.id,
          person_type: metadata.personType ? metadata.personType : undefined,
          loan_application_id: application.id,
        };

        let signUrlResponse;

        if (uploadService === "second_guarantee") {
          signUrlResponse = await triggerGetSignUrlSecondGuarantee(payloadSignUrl).unwrap();
        } else {
          signUrlResponse = await triggerGetSignUrl(payloadSignUrl).unwrap();
        }

        const tmp = new FormData();

        tmp.append("key", signUrlResponse.fields.key);
        tmp.append("AWSAccessKeyId", signUrlResponse.fields.AWSAccessKeyId);
        tmp.append("x-amz-security-token", signUrlResponse.fields["x-amz-security-token"]);
        tmp.append("policy", signUrlResponse.fields.policy);
        tmp.append("signature", signUrlResponse.fields.signature);
        tmp.append("file", file);

        // subir el archivo
        const payloadUploadFile: PayloadUploadFile = {
          url: signUrlResponse.url,
          body: tmp,
        };

        if (uploadService === "second_guarantee") {
          await triggerUploadFileSecondGuarantee(payloadUploadFile).unwrap();
        } else {
          await triggerUploadFile(payloadUploadFile).unwrap();
        }

        // guardar el documento
        const payloadPostDocument: PayloadDocument = {
          loan_application_id: application.id,
          file_category_id: fileCategory.id,
          client_details_id: metadata.uploadType === "client" ? metadata.clientDetailsId : undefined,
          person_id: metadata.uploadType === "person" ? metadata.personId : undefined,
          guarantee_id: metadata.guaranteeId || undefined,
          url: signUrlResponse.fields.key,
          file_name: fileName,
        };
        let documentResponse;

        if (uploadService === "second_guarantee") {
          documentResponse = await triggerPostDocumentSecondGuarantee(payloadPostDocument).unwrap();
        } else {
          documentResponse = await triggerPostDocument(payloadPostDocument).unwrap();
        }

        setUploadedFile(documentResponse);
      }
      dispatch(setUploadModalErrorVisible({ file_name: "", visible: false }));
    } catch (error) {
      console.log("Ha ocurrido un error al subir el archivo", error);
      dispatch(setUploadModalErrorVisible({ file_name: fileName ? fileName : "", visible: true }));
    } finally {
      event.target.value = "";
      setLoading(false);
    }
  };

  const handleOnClickDelete = async () => {
    try {
      if (!uploadedFile) return;

      setLoading(true);

      if (uploadService === "second_guarantee") {
        await triggerPatchDocumentSecondGuarantee({
          documentId: uploadedFile.id,
          body: { status: false },
        }).unwrap();
      } else {
        await triggerPatchDocument({
          documentId: uploadedFile.id,
          body: { status: false },
        }).unwrap();
      }

      setUploadedFile(undefined);
    } catch (error) {
      console.log("Ha ocurrido un error al eliminar el archivo", error);
      const apiError = error as ErrorResponse;
      if (apiError.status === 403) {
        setErrorMessage("El archivo no pudo ser eliminado debido a que se encuentra validado");
        if (uploadedFile) {
          setUploadedFile({ ...uploadedFile, validated: true });
        }
      } else {
        setErrorMessage("Ha ocurrido un error al eliminar el archivo");
      }
      setShowErrorMessage(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOnReuploaded = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    try {
      if (!uploadedFile) return;

      setLoading(true);

      if (uploadService === "second_guarantee") {
        await triggerPatchDocumentSecondGuarantee({
          documentId: uploadedFile.id,
          body: { status: false },
        }).unwrap();
      } else {
        await triggerPatchDocument({
          documentId: uploadedFile.id,
          body: { status: false },
        }).unwrap();
      }

      await handleOnChange(event);
    } catch (error) {
      console.log("Ha ocurrido un error al re-subir el archivo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (documentConfig.isSuccess) {
      const fileCategory = documentConfig.data.find((file) => file.code === metadata.fileCode);
      setFileCategory(fileCategory);
    }
  }, [documentConfig]);

  return (
    <Box {...props}>
      <Grid container>
        <Grid item xs={8} pr={2}>
          <Typography
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={theme?.palette?.fontWeights?.bold}
            textAlign={"left"}
            color={theme?.palette?.primary?.dark}
          >
            {documentConfig.isFetching ? (
              <Skeleton variant="text" animation={"wave"} />
            ) : (
              <>
                {optionalName ? optionalName : fileCategory?.name}{" "}
                {required && (
                  <Typography component={"span"} color={"#CD0202"}>
                    *
                  </Typography>
                )}
              </>
            )}
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <Box display="flex" justifyContent="flex-end">
            <label htmlFor={`upload-file_${inputName}`}>
              <input
                type="file"
                id={`upload-file_${inputName}`}
                name={`upload-file_${inputName}`}
                accept="application/pdf,image/jpeg,image/png"
                hidden
                onChange={uploadedFile?.validated === false ? handleOnReuploaded : handleOnChange}
                disabled={loading || documentConfig.isFetching || disabled}
              />

              {!uploadedFile?.id && (
                <Button
                  variant="expediente"
                  size={isMobileOrTablet ? "small" : "medium"}
                  component={"span"}
                  disabled={loading || documentConfig.isFetching || disabled}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "white", paddingTop: "5px", paddingBottom: "5px" }} />
                  ) : (
                    "Cargar"
                  )}
                </Button>
              )}

              {uploadedFile?.id && uploadedFile?.validated === false && (
                <Button
                  variant="expediente"
                  size={isMobileOrTablet ? "small" : "medium"}
                  component={"span"}
                  sx={{ backgroundColor: theme.palette.primary?.accent, width: isMobileOrTablet ? "150px" : "160px" }}
                  disabled={loading || documentConfig.isFetching || disabled}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "white", paddingTop: "5px", paddingBottom: "5px" }} />
                  ) : (
                    "Volver a cargar"
                  )}
                </Button>
              )}
            </label>
          </Box>
        </Grid>
      </Grid>

      {uploadedFile?.id && uploadedFile.validated !== false && (
        <Grid container>
          <Grid item xs={4} textAlign="left">
            <Typography
              component={"span"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={"#00A024"}
              fontWeight={theme?.palette?.fontWeights?.regular}
            >
              Archivo cargado:
            </Typography>
          </Grid>
          <Grid item xs={7} textAlign="left">
            <Typography
              noWrap
              display={"inline-block"}
              maxWidth={"100%"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              fontWeight={theme?.palette?.fontWeights?.regular}
            >
              {uploadedFile.file_name}
            </Typography>
          </Grid>
          <Grid item xs={1} display="flex" flexDirection="row" alignItems="center">
            <img src={Done} width={20} height={15} />
          </Grid>
        </Grid>
      )}

      <Grid container>
        <Grid item xs={7} textAlign={"left"} pr={4}>
          <Typography
            variant={isMobileOrTablet ? "body3" : "body2"}
            fontWeight={theme?.palette?.fontWeights?.regular}
            color={theme?.palette?.primary?.dark}
          >
            {documentConfig.isFetching ? (
              <Skeleton variant="text" animation={"wave"} />
            ) : (
              optionalDescription ?? fileCategory?.description
            )}
          </Typography>
        </Grid>

        <Grid item xs={5} display="flex" justifyContent="flex-end">
          {uploadedFile?.id && (
            <Box>
              {uploadedFile?.validated === null && (
                <Button
                  variant="expediente"
                  onClick={handleOnClickDelete}
                  sx={{
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.primary.light,
                  }}
                  disabled={loading || documentConfig.isFetching || disabled}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "white", paddingTop: "5px", paddingBottom: "5px" }} />
                  ) : (
                    "Eliminar"
                  )}
                </Button>
              )}
              {uploadedFile?.validated === true && (
                <Button
                  sx={{
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.main,
                  }}
                  variant="expediente"
                  size={isMobileOrTablet ? "small" : "medium"}
                >
                  Validado
                </Button>
              )}
            </Box>
          )}
        </Grid>
      </Grid>

      {!hiddeDivider && <Divider sx={{ backgroundColor: "#A3D4E8", mt: 2 }} />}

      <Dialog open={showErrorMessage && errorMessage !== ""} maxWidth="xs">
        <DialogTitle>
          <Box position={"relative"} mt={2}>
            <IconButton sx={{ position: "absolute", top: -20, right: -20 }} onClick={() => setShowErrorMessage(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              textAlign={"center"}
            >
              {errorMessage}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="contained" color="primary" onClick={() => setShowErrorMessage(false)}>
              Aceptar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CustomUploadFileV2;
